import { Injectable, computed, signal } from '@angular/core';
import { ApiService } from './api.service';
import { Brand, OrganizationCreditSummary, WorkspaceContext } from './models';

const UNLIMITED_CREDITS: OrganizationCreditSummary = {
  organizationId: '',
  runs: 0,
  completedRuns: 0,
  remaining: 0,
  limitReached: false,
  unlimited: true,
};

@Injectable({
  providedIn: 'root',
})
export class WorkspaceService {
  private static readonly PRO_ENABLED_KEY = 'acl-pro-enabled';

  private readonly contextState = signal<WorkspaceContext | null>(null);
  private readonly loadingState = signal(false);
  private readonly errorState = signal<string | null>(null);
  private readonly proEnabledState = signal(this.storedProEnabled());

  readonly context = computed(() => this.contextState());
  readonly loading = computed(() => this.loadingState());
  readonly error = computed(() => this.errorState());
  readonly brands = computed(() => this.contextState()?.brands ?? []);
  readonly organization = computed(() => this.contextState()?.organization ?? null);
  readonly activeBrand = computed(() => this.contextState()?.activeBrand ?? null);
  readonly user = computed(() => this.contextState()?.user ?? null);
  readonly rights = computed(() => this.contextState()?.rights ?? { pro: false });
  readonly proGranted = computed(() => this.rights().pro);
  readonly proEnabled = computed(() => this.proEnabledState());
  readonly isPro = computed(() => this.proGranted() && this.proEnabled());
  readonly hasWorkspace = computed(() => Boolean(this.contextState()?.activeBrand));
  readonly credits = computed<OrganizationCreditSummary>(
    () => this.contextState()?.credits ?? UNLIMITED_CREDITS,
  );

  constructor(private readonly apiService: ApiService) {}

  async loadContext(): Promise<void> {
    this.loadingState.set(true);
    this.errorState.set(null);

    try {
      const context = await this.apiService.getContext();
      if (context.activeBrand) {
        localStorage.setItem('activeBrandId', context.activeBrand.id);
      }
      this.contextState.set(context);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load workspace context.';
      this.errorState.set(message);
      this.contextState.set(null);
      throw error;
    } finally {
      this.loadingState.set(false);
    }
  }

  async switchBrand(brandId: string): Promise<void> {
    localStorage.setItem('activeBrandId', brandId);
    await this.loadContext();
  }

  async refreshCredits(): Promise<void> {
    const current = this.contextState();
    if (!current) {
      return;
    }

    try {
      const credits = await this.apiService.getOrganizationCredits();
      this.contextState.set({ ...current, credits });
    } catch {
      /* silent: keep current credits */
    }
  }

  isActiveBrand(brand: Brand): boolean {
    return this.activeBrand()?.id === brand.id;
  }

  clear(): void {
    this.contextState.set(null);
    this.errorState.set(null);
    localStorage.removeItem('activeBrandId');
  }

  setProEnabled(enabled: boolean): void {
    this.proEnabledState.set(enabled);
    localStorage.setItem(WorkspaceService.PRO_ENABLED_KEY, String(enabled));
  }

  private storedProEnabled(): boolean {
    const stored = localStorage.getItem(WorkspaceService.PRO_ENABLED_KEY);
    return stored === null ? true : stored === 'true';
  }
}
